import { useState, useEffect } from 'react';
import apiService from '../../../services/api';

interface ImplementationOverviewProps {
  onTooltipShow: (title: string, content: string) => void;
  onNavigate: (section: string) => void;
  darkMode?: boolean;
}

export default function ImplementationOverview({ onTooltipShow, onNavigate, darkMode }: ImplementationOverviewProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [showVideo, setShowVideo] = useState(false);
  const userId = localStorage.getItem('user_id') || '';


  interface ModuleStat {
    target: number;
    posted: number;
    pending: number;
    total: number;
    created: number;
    pct: number;
  }

  interface OverviewCategoryData {
    target: number;
    total: number;
    created: number;
    posted: number;
    pending: number;
    pct: number;
    modules: string[];
    module_stats: Record<string, ModuleStat>;
  }

  const [onPageData, setOnPageData] = useState<OverviewCategoryData>({
    target: 8,
    total: 8,
    created: 0,
    posted: 0,
    pending: 8,
    pct: 0,
    modules: ['Image Optimizer', 'Meta Title Generator', 'Blog', 'FAQ'],
    module_stats: {},
  });

  const [offPageData, setOffPageData] = useState<OverviewCategoryData>({
    target: 20,
    total: 20,
    created: 0,
    posted: 0,
    pending: 20,
    pct: 0,
    modules: [
      'Social Bookmarking',
      'Profile Listings',
      'Directory Submission',
      'Q&A Submission',
      'Web 2.0 Articles',
      'Classified Ads',
      'Image Submission',
      'PDF Submission',
    ],
    module_stats: {},
  });

  // Tooltip: fixed-position so it escapes the card's overflow-hidden
  interface FixedTooltipPos { left: number; bottom: number; arrowLeft: number; }
  const [hoveredModule, setHoveredModule] = useState<string | null>(null);
  const [fixedPos, setFixedPos] = useState<FixedTooltipPos | null>(null);

  const handleChipMouseEnter = (mod: string, e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const TW = 208;   // tooltip width (w-52)
    const GAP = 10;   // gap between chip top and tooltip bottom
    const MARGIN = 8; // minimum distance from viewport edge

    const chipCx = rect.left + rect.width / 2;
    let left = chipCx - TW / 2;
    // Clamp inside viewport
    if (left < MARGIN) left = MARGIN;
    if (left + TW > window.innerWidth - MARGIN) left = window.innerWidth - TW - MARGIN;
    // Arrow should point at chip centre; clamp so it stays within tooltip body
    const arrowLeft = Math.max(10, Math.min(chipCx - left - 6, TW - 22));

    setFixedPos({
      left,
      bottom: window.innerHeight - rect.top + GAP,
      arrowLeft,
    });
    setHoveredModule(mod);
  };

  useEffect(() => {
    fetchOverview(true);
  }, [userId]);

  const fetchOverview = async (isInitial = false) => {
    if (!userId) return;
    if (isInitial) setIsLoading(true);
    try {
      const res = await apiService.getImplementationOverview(userId);
      if (res.status === 'success') {
        setOnPageData(res.data.on_page);
        setOffPageData(res.data.off_page);
      }
    } catch (error) {
      console.error('Failed to fetch overview data:', error);
    } finally {
      if (isInitial) setIsLoading(false);
    }
  };

  const onPageCompletion = onPageData.pct ?? (onPageData.target > 0 ? Math.round((onPageData.posted / onPageData.target) * 100) : 0);
  const offPageCompletion = offPageData.pct ?? (offPageData.target > 0 ? Math.round((offPageData.posted / offPageData.target) * 100) : 0);

  const onPageVisualPct = Math.min(100, Math.max(0, onPageCompletion));
  const offPageVisualPct = Math.min(100, Math.max(0, offPageCompletion));


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Content Implementation Overview</h2>
          <button
            onClick={() => onTooltipShow('Implementation Overview', 'Track and manage your on-page and off-page content implementation progress. On-page covers website elements like images, meta tags, blogs, and FAQs. Off-page covers external submissions like articles, classifieds, bookmarks, and directory listings.')}
            className="text-gray-400 hover:text-blue-600 cursor-pointer"
          >
            <i className="ri-information-line text-xl"></i>
          </button>
        </div>
        <p className="text-gray-600 dark:text-gray-400">Track the progress of your on-page and off-page content implementation</p>
      </div>

      {/* Thin Video Section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
        <div className="px-6 py-3 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-play-circle-line text-white text-xl"></i>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">How Implementation Dashboard Works</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">3-4 min tutorial</p>
            </div>
          </div>
          <button
            disabled
            className="px-4 py-2 bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400 text-sm font-medium rounded-lg transition-colors flex items-center space-x-2 whitespace-nowrap cursor-not-allowed"
          >
            <i className="ri-vidicon-line"></i>
            <span>Video Coming Soon</span>
          </button>
        </div>

      </div>

      {/* Two Cards: On-Page & Off-Page */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* On-Page Content Card */}
        <button
          onClick={() => onNavigate('on-page-content')}
          className={`rounded-2xl overflow-hidden text-left transition-all duration-200 hover:shadow-md cursor-pointer ${
            darkMode ? 'bg-gray-800 border border-gray-700 hover:border-blue-600' : 'bg-white border border-gray-200 hover:border-blue-400'
          }`}
        >
          <div className="p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center">
                  <i className="ri-pages-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    On-Page Content
                  </h3>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {isLoading ? (
                      <span className="inline-block h-3 bg-gray-200 dark:bg-gray-700 rounded w-24 animate-pulse"></span>
                    ) : (
                      `${onPageData.posted} / ${onPageData.target} posted · ${onPageData.modules.length} modules`
                    )}
                  </p>
                </div>
              </div>
              <i className={`ri-arrow-right-line text-xl ${darkMode ? 'text-gray-500' : 'text-gray-300'} group-hover:text-blue-600 transition-colors`}></i>
            </div>

            {/* Progress Circle + Stats */}
            {isLoading ? (
              <div className="flex items-center gap-8 mb-5 animate-pulse">
                <div className="w-32 h-32 rounded-full bg-gray-200 dark:bg-gray-700 flex-shrink-0 flex items-center justify-center">
                  <div className="w-20 h-20 rounded-full bg-white dark:bg-gray-800"></div>
                </div>
                <div className="flex-1 space-y-4">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-8 mb-5">
                <div className="relative w-32 h-32 flex-shrink-0">
                  <svg className="w-32 h-32 transform -rotate-90">
                    <circle
                      cx="64" cy="64" r="56"
                      stroke={darkMode ? '#374151' : '#E5E7EB'}
                      strokeWidth="12"
                      fill="none"
                    />
                    <circle
                      cx="64" cy="64" r="56"
                      stroke="#0859B8"
                      strokeWidth="12"
                      fill="none"
                      strokeDasharray={`${2 * Math.PI * 56}`}
                      strokeDashoffset={`${2 * Math.PI * 56 * (1 - onPageVisualPct / 100)}`}
                      strokeLinecap="round"
                      className="transition-all duration-1000"
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {onPageCompletion}%
                    </span>
                    <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Complete
                    </span>
                  </div>
                </div>

                <div className="flex-1 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full bg-emerald-500"></div>
                      <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Posted</span>
                    </div>
                    <span className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {onPageData.posted}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full bg-gray-300 dark:bg-gray-500"></div>
                      <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Pending</span>
                    </div>
                    <span className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {onPageData.pending}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Module Chips */}
            {isLoading ? (
              <div className="flex flex-wrap gap-1.5 mb-4 animate-pulse">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-5 bg-gray-200 dark:bg-gray-700 rounded-full w-20"></div>
                ))}
              </div>
            ) : (
              <div className="flex flex-wrap gap-1.5 mb-4">
                {onPageData.modules.map((mod) => {
                  const stat = onPageData.module_stats?.[mod];
                  const R = 22;
                  const circ = 2 * Math.PI * R;
                  const pct = stat?.pct ?? 0;
                  const visualPct = Math.min(100, Math.max(0, pct));
                  const postedArc = circ * (visualPct / 100);

                  return (
                    <div
                      key={mod}
                      className="relative"
                      onMouseEnter={(e) => handleChipMouseEnter(mod, e)}
                      onMouseLeave={() => { setHoveredModule(null); setFixedPos(null); }}
                    >
                      <span
                        className={`inline-block px-2.5 py-0.5 text-[11px] font-medium rounded-full border cursor-default select-none transition-all duration-150 ${
                          darkMode
                            ? 'bg-gray-700 border-gray-600 text-gray-300 hover:border-blue-500 hover:text-blue-300'
                            : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-blue-400 hover:text-blue-700'
                        }`}
                      >
                        {mod}
                      </span>

                      {/* Hover tooltip — fixed-positioned to escape overflow:hidden on parent card */}
                      {hoveredModule === mod && stat && fixedPos && (
                        <div
                          className={`rounded-xl border pointer-events-none ${
                            darkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
                          }`}
                          style={{
                            position: 'fixed',
                            left: fixedPos.left,
                            bottom: fixedPos.bottom,
                            width: 208,
                            zIndex: 9999,
                            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                          }}
                        >
                          {/* Arrow */}
                          <div
                            className={`absolute -bottom-[6px] w-3 h-3 rotate-45 border-b border-r ${
                              darkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
                            }`}
                            style={{ left: fixedPos.arrowLeft }}
                          />
                          <div className="px-4 py-3">
                            <p className={`text-[11px] font-semibold mb-2.5 truncate ${
                              darkMode ? 'text-gray-300' : 'text-gray-500'
                            }`}>{mod}</p>

                            <div className="flex items-center gap-4">
                              {/* Mini donut chart — blue for on-page */}
                              <div className="relative flex-shrink-0">
                                <svg width={56} height={56} className="-rotate-90" style={{ display: 'block' }}>
                                  <circle cx={28} cy={28} r={R} fill="none"
                                    stroke={darkMode ? '#374151' : '#E5E7EB'} strokeWidth={7} />
                                  <circle cx={28} cy={28} r={R} fill="none"
                                    stroke="#0859B8" strokeWidth={7}
                                    strokeDasharray={`${postedArc} ${circ}`}
                                    strokeLinecap="round"
                                    style={{ transition: 'stroke-dasharray 0.4s ease' }} />
                                </svg>
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <span className={`text-[11px] font-bold ${
                                    darkMode ? 'text-white' : 'text-gray-800'
                                  }`}>{pct}%</span>
                                </div>
                              </div>

                              {/* Counts */}
                              <div className="flex-1 space-y-1.5">
                                <div className="flex items-center justify-between gap-2">
                                  <div className="flex items-center gap-1.5">
                                    <div className="w-2 h-2 rounded-full bg-blue-600 flex-shrink-0" />
                                    <span className={`text-[11px] ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Posted</span>
                                  </div>
                                  <span className={`text-[12px] font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>{stat.posted}</span>
                                </div>
                                <div className="flex items-center justify-between gap-2">
                                  <div className="flex items-center gap-1.5">
                                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${darkMode ? 'bg-gray-600' : 'bg-gray-300'}`} />
                                    <span className={`text-[11px] ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Pending</span>
                                  </div>
                                  <span className={`text-[12px] font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>{stat.pending}</span>
                                </div>
                                {stat.target > 0 && (
                                  <div className={`text-[10px] pt-0.5 border-t ${
                                    darkMode ? 'text-gray-500 border-gray-700' : 'text-gray-400 border-gray-100'
                                  }`}>
                                    {stat.posted} / {stat.target} target · {stat.created} created
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* View Details CTA */}
            <div className={`w-full py-2.5 rounded-lg text-center text-sm font-medium transition-colors ${
              darkMode
                ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
            }`}>
              View Details <i className="ri-arrow-right-line ml-1"></i>
            </div>
          </div>
        </button>

        {/* Off-Page Content Card */}
        <button
          onClick={() => onNavigate('off-page-content')}
          className={`rounded-2xl overflow-hidden text-left transition-all duration-200 hover:shadow-md cursor-pointer ${
            darkMode ? 'bg-gray-800 border border-gray-700 hover:border-cyan-600' : 'bg-white border border-gray-200 hover:border-cyan-400'
          }`}
        >
          <div className="p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-600 to-blue-500 flex items-center justify-center">
                  <i className="ri-share-forward-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    Off-Page Content
                  </h3>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {isLoading ? (
                      <span className="inline-block h-3 bg-gray-200 dark:bg-gray-700 rounded w-24 animate-pulse"></span>
                    ) : (
                      `${offPageData.posted} / ${offPageData.target} posted · ${offPageData.modules.length} modules`
                    )}
                  </p>
                </div>
              </div>
              <i className={`ri-arrow-right-line text-xl ${darkMode ? 'text-gray-500' : 'text-gray-300'} group-hover:text-cyan-600 transition-colors`}></i>
            </div>

            {/* Progress Circle + Stats */}
            {isLoading ? (
              <div className="flex items-center gap-8 mb-5 animate-pulse">
                <div className="w-32 h-32 rounded-full bg-gray-200 dark:bg-gray-700 flex-shrink-0 flex items-center justify-center">
                  <div className="w-20 h-20 rounded-full bg-white dark:bg-gray-800"></div>
                </div>
                <div className="flex-1 space-y-4">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-8 mb-5">
                <div className="relative w-32 h-32 flex-shrink-0">
                  <svg className="w-32 h-32 transform -rotate-90">
                    <circle
                      cx="64" cy="64" r="56"
                      stroke={darkMode ? '#374151' : '#E5E7EB'}
                      strokeWidth="12"
                      fill="none"
                    />
                    <circle
                      cx="64" cy="64" r="56"
                      stroke="#00ADC4"
                      strokeWidth="12"
                      fill="none"
                      strokeDasharray={`${2 * Math.PI * 56}`}
                      strokeDashoffset={`${2 * Math.PI * 56 * (1 - offPageVisualPct / 100)}`}
                      strokeLinecap="round"
                      className="transition-all duration-1000"
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {offPageCompletion}%
                    </span>
                    <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Complete
                    </span>
                  </div>
                </div>

                <div className="flex-1 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full bg-cyan-500"></div>
                      <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Posted</span>
                    </div>
                    <span className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {offPageData.posted}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full bg-gray-300 dark:bg-gray-500"></div>
                      <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Pending</span>
                    </div>
                    <span className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {offPageData.pending}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Module Chips */}
            {isLoading ? (
              <div className="flex flex-wrap gap-1.5 mb-4 animate-pulse">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="h-5 bg-gray-200 dark:bg-gray-700 rounded-full w-20"></div>
                ))}
              </div>
            ) : (
              <div className="flex flex-wrap gap-1.5 mb-4">
                {offPageData.modules.map((mod) => {
                  const stat = offPageData.module_stats?.[mod];
                  const R = 22;
                  const circ = 2 * Math.PI * R;
                  const pct = stat?.pct ?? 0;
                  const visualPct = Math.min(100, Math.max(0, pct));
                  const postedArc = circ * (visualPct / 100);

                  return (
                    <div
                      key={mod}
                      className="relative"
                      onMouseEnter={(e) => handleChipMouseEnter(mod, e)}
                      onMouseLeave={() => { setHoveredModule(null); setFixedPos(null); }}
                    >
                      <span
                        className={`inline-block px-2.5 py-0.5 text-[11px] font-medium rounded-full border cursor-default select-none transition-all duration-150 ${
                          darkMode
                            ? 'bg-gray-700 border-gray-600 text-gray-300 hover:border-cyan-500 hover:text-cyan-300'
                            : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-cyan-400 hover:text-cyan-700'
                        }`}
                      >
                        {mod}
                      </span>

                      {/* Hover tooltip — fixed-positioned to escape overflow:hidden on parent card */}
                      {hoveredModule === mod && stat && fixedPos && (
                        <div
                          className={`rounded-xl border pointer-events-none ${
                            darkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
                          }`}
                          style={{
                            position: 'fixed',
                            left: fixedPos.left,
                            bottom: fixedPos.bottom,
                            width: 208,
                            zIndex: 9999,
                            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                          }}
                        >
                          {/* Arrow — points down at the chip */}
                          <div
                            className={`absolute -bottom-[6px] w-3 h-3 rotate-45 border-b border-r ${
                              darkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
                            }`}
                            style={{ left: fixedPos.arrowLeft }}
                          />
                          <div className="px-4 py-3">
                            <p className={`text-[11px] font-semibold mb-2.5 truncate ${
                              darkMode ? 'text-gray-300' : 'text-gray-500'
                            }`}>{mod}</p>

                            <div className="flex items-center gap-4">
                              {/* Mini donut chart */}
                              <div className="relative flex-shrink-0">
                                <svg width={56} height={56} className="-rotate-90" style={{ display: 'block' }}>
                                  <circle cx={28} cy={28} r={R} fill="none"
                                    stroke={darkMode ? '#374151' : '#E5E7EB'} strokeWidth={7} />
                                  <circle cx={28} cy={28} r={R} fill="none"
                                    stroke="#00ADC4" strokeWidth={7}
                                    strokeDasharray={`${postedArc} ${circ}`}
                                    strokeLinecap="round"
                                    style={{ transition: 'stroke-dasharray 0.4s ease' }} />
                                </svg>
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <span className={`text-[11px] font-bold ${
                                    darkMode ? 'text-white' : 'text-gray-800'
                                  }`}>{pct}%</span>
                                </div>
                              </div>

                              {/* Counts */}
                              <div className="flex-1 space-y-1.5">
                                <div className="flex items-center justify-between gap-2">
                                  <div className="flex items-center gap-1.5">
                                    <div className="w-2 h-2 rounded-full bg-cyan-500 flex-shrink-0" />
                                    <span className={`text-[11px] ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Posted</span>
                                  </div>
                                  <span className={`text-[12px] font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>{stat.posted}</span>
                                </div>
                                <div className="flex items-center justify-between gap-2">
                                  <div className="flex items-center gap-1.5">
                                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${darkMode ? 'bg-gray-600' : 'bg-gray-300'}`} />
                                    <span className={`text-[11px] ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Pending</span>
                                  </div>
                                  <span className={`text-[12px] font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>{stat.pending}</span>
                                </div>
                                {stat.target > 0 && (
                                  <div className={`text-[10px] pt-0.5 border-t ${
                                    darkMode ? 'text-gray-500 border-gray-700' : 'text-gray-400 border-gray-100'
                                  }`}>
                                    {stat.posted} / {stat.target} target · {stat.created} created
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* View Details CTA */}
            <div className={`w-full py-2.5 rounded-lg text-center text-sm font-medium transition-colors ${
              darkMode
                ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
            }`}>
              View Details <i className="ri-arrow-right-line ml-1"></i>
            </div>
          </div>
        </button>
      </div>
    </div>
  );
}
